<template>
  <div class="login-container">
    <el-card class="login-box">
      <h2 class="login-title">进销存管理系统</h2>
      
      <!-- 登录方式切换 -->
      <el-tabs v-model="loginType" class="login-tabs">
        <el-tab-pane label="密码登录" name="password">
          <!-- autocomplete + Credential Management：密码只进系统密码库，不写网页本地 -->
          <el-form
            :model="passwordForm"
            :rules="passwordRules"
            ref="passwordFormRef"
            label-width="0"
            autocomplete="on"
            class="login-native-form"
            @submit.prevent="handlePasswordLogin"
          >
            <el-form-item prop="username">
              <el-input
                v-model="passwordForm.username"
                name="username"
                autocomplete="username"
                placeholder="登录用户名（不是姓名）"
                size="large"
                clearable
              >
                <template #prefix>
                  <el-icon><User /></el-icon>
                </template>
              </el-input>
            </el-form-item>
            <el-form-item prop="password">
              <el-input
                v-model="passwordForm.password"
                name="password"
                type="password"
                autocomplete="current-password"
                placeholder="密码"
                size="large"
                show-password
                clearable
                @keyup.enter="handlePasswordLogin"
              >
                <template #prefix>
                  <el-icon><Lock /></el-icon>
                </template>
              </el-input>
            </el-form-item>
            <el-form-item>
              <el-checkbox v-model="rememberUsername">记住用户名</el-checkbox>
              <p v-if="showSecurePasswordHint" class="login-secure-hint">
                主屏幕 APP / 安卓：登录成功后可用系统「保存密码」；密码只保存在本机密码库，不会上传到业务服务器。
              </p>
            </el-form-item>
            <el-form-item>
              <el-button 
                type="primary" 
                size="large" 
                native-type="submit"
                :loading="loading"
                style="width: 100%"
              >
                {{ loading ? '登录中...' : '登录' }}
              </el-button>
            </el-form-item>
            <el-form-item>
              <div class="login-help-row">
                <el-button link type="primary" @click="openResetDialog">忘记密码</el-button>
                <span class="help-text">请输入登录用户名，不是姓名或账户备注名称</span>
              </div>
            </el-form-item>
          </el-form>
        </el-tab-pane>
        
        <el-tab-pane label="短信登录" name="sms">
          <el-form :model="smsForm" :rules="smsRules" ref="smsFormRef" label-width="0">
            <el-form-item prop="phone">
              <el-input
                v-model="smsForm.phone"
                placeholder="手机号"
                size="large"
                clearable
                maxlength="11"
              >
                <template #prefix>
                  <el-icon><Phone /></el-icon>
                </template>
              </el-input>
            </el-form-item>
            <el-form-item prop="sms_code">
              <div class="sms-code-input">
                <el-input
                  v-model="smsForm.sms_code"
                  placeholder="验证码"
                  size="large"
                  clearable
                  maxlength="6"
                  @keyup.enter="handleSmsLogin"
                >
                  <template #prefix>
                    <el-icon><Message /></el-icon>
                  </template>
                </el-input>
                <el-button
                  :disabled="smsCountdown > 0"
                  @click="sendSmsCode"
                  :loading="sendingSms"
                  class="sms-button"
                >
                  {{ smsCountdown > 0 ? `${smsCountdown}秒后重试` : '获取验证码' }}
                </el-button>
              </div>
            </el-form-item>
            <el-form-item>
              <el-button 
                type="primary" 
                size="large" 
                @click="handleSmsLogin"
                :loading="loading"
                style="width: 100%"
              >
                {{ loading ? '登录中...' : '登录' }}
              </el-button>
            </el-form-item>
          </el-form>
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <el-dialog v-model="showResetDialog" title="短信重置密码" width="460px">
      <el-alert
        title="若手机号收不到验证码，请联系管理员在“用户管理”中执行重置密码。"
        type="warning"
        :closable="false"
        style="margin-bottom: 12px"
      />
      <el-alert :title="PASSWORD_POLICY_HINT" type="info" :closable="false" style="margin-bottom: 12px" />
      <el-form :model="resetForm" :rules="resetRules" ref="resetFormRef" label-width="90px">
        <el-form-item label="手机号" prop="phone">
          <el-input v-model="resetForm.phone" maxlength="11" />
        </el-form-item>
        <el-form-item label="验证码" prop="sms_code">
          <div class="sms-code-input">
            <el-input v-model="resetForm.sms_code" maxlength="6" />
            <el-button
              :disabled="resetSmsCountdown > 0"
              @click="sendResetSmsCode"
              :loading="sendingResetSms"
              class="sms-button"
            >
              {{ resetSmsCountdown > 0 ? `${resetSmsCountdown}秒后重试` : '获取验证码' }}
            </el-button>
          </div>
        </el-form-item>
        <el-form-item label="新密码" prop="new_password">
          <el-input
            v-model="resetForm.new_password"
            type="password"
            autocomplete="new-password"
            show-password
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showResetDialog = false">取消</el-button>
        <el-button type="primary" :loading="resettingPassword" @click="handleResetPasswordBySms">
          重置密码
        </el-button>
      </template>
    </el-dialog>

    <SiteBeianFooter tone="on-dark" fixed />
  </div>
</template>

<script setup>
import { onMounted, ref, reactive, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { User, Lock, Phone, Message } from '@element-plus/icons-vue'
import { useRouter, useRoute } from 'vue-router'
import axios from 'axios'
import request from '@/utils/request'
import { APP_BUILD_ID, markUserActive } from '@/utils/auth'
import { useUserStore } from '@/store/user'
import { isLikelyMobileBrowser, resolvePostLoginPath } from '@/utils/device'
import { canAccessMobilePath } from '@/utils/mobileAccess'
import { resetBodyElLoadingArtifacts } from '@/utils/elementLoadingCleanup'
import { PASSWORD_POLICY_HINT, validatePasswordClient } from '@/utils/passwordPolicy'
import {
  tryLoadPasswordFromSystemVault,
  tryStorePasswordInSystemVault
} from '@/utils/loginCredentials'
import { isStandalonePwaShell } from '@/utils/device'
import SiteBeianFooter from '@/components/SiteBeianFooter.vue'

const router = useRouter()
const userStore = useUserStore()
const route = useRoute()

function navigatePostLogin(path) {
  const p = String(path || '/')
  if (p === '/login') return
  /** 移动端进工作台后用 replace，避免系统返回手势回到登录空白页误认为已退出 */
  if (isLikelyMobileBrowser() && p.startsWith('/m')) {
    router.replace(p)
    return
  }
  router.push(p)
}
const loading = ref(false)
const sendingSms = ref(false)
const smsCountdown = ref(0)
const showResetDialog = ref(false)
const sendingResetSms = ref(false)
const resetSmsCountdown = ref(0)
const resettingPassword = ref(false)
const loginType = ref('password')
const passwordFormRef = ref(null)
const smsFormRef = ref(null)
const resetFormRef = ref(null)
const rememberUsername = ref(true)
const REMEMBER_USERNAME_KEY = 'erp_login_remember_username_v1'
const REMEMBERED_USERNAME_KEY = 'erp_login_remembered_username_v1'
const showSecurePasswordHint = ref(
  typeof window !== 'undefined' &&
    (isStandalonePwaShell() || isLikelyMobileBrowser())
)

const passwordForm = reactive({
  username: '',
  password: ''
})

const smsForm = reactive({
  phone: '',
  sms_code: ''
})

const resetForm = reactive({
  phone: '',
  sms_code: '',
  new_password: ''
})

const passwordRules = reactive({
  username: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
  password: [{ required: true, message: '请输入密码', trigger: 'blur' }]
})

const smsRules = reactive({
  phone: [
    { required: true, message: '请输入手机号', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
  ],
  sms_code: [
    { required: true, message: '请输入验证码', trigger: 'blur' },
    { pattern: /^\d{6}$/, message: '请输入6位数字验证码', trigger: 'blur' }
  ]
})

const resetRules = reactive({
  phone: [
    { required: true, message: '请输入手机号', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
  ],
  sms_code: [
    { required: true, message: '请输入验证码', trigger: 'blur' },
    { pattern: /^\d{6}$/, message: '请输入6位数字验证码', trigger: 'blur' }
  ],
  new_password: [
    { required: true, message: '请输入新密码', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        const err = validatePasswordClient(value)
        if (err) callback(new Error(err))
        else callback()
      },
      trigger: 'blur'
    }
  ]
})

const startCountdown = (targetRef) => {
  targetRef.value = 60
  const timer = setInterval(() => {
    targetRef.value--
    if (targetRef.value <= 0) clearInterval(timer)
  }, 1000)
}

const inferDeviceType = () => {
  if (window.location.pathname.startsWith('/m/')) return 'mobile'
  return isLikelyMobileBrowser() ? 'mobile' : 'desktop'
}

const getDeviceFingerprint = () => {
  const k = 'device_fingerprint_v1'
  let v = localStorage.getItem(k)
  if (!v) {
    v = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
    localStorage.setItem(k, v)
  }
  return v
}

const getDeviceName = () => {
  const typeLabel = inferDeviceType() === 'mobile' ? '手机端' : '电脑端'
  const ua = navigator.userAgent || 'Unknown'
  return `${typeLabel}-${ua.slice(0, 48)}`
}

const withSessionControlLogin = async (payloadBuilder) => {
  const basePayload = payloadBuilder(false)
  try {
    return await request({
      url: '/api/auth/login',
      method: 'post',
      data: basePayload
    })
  } catch (error) {
    const conflict = error?.response?.status === 409 && error?.response?.data?.code === 'ACTIVE_SESSION_EXISTS'
    if (!conflict) throw error
    const info = error.response.data?.existing_session || {}
    const where = info.device_type === 'mobile' ? '手机端' : '电脑端'
    const ip = info.client_ip || '未知IP'
    const at = info.logged_in_at || '未知时间'
    await ElMessageBox.confirm(
      `该账号在${where}已登录（IP: ${ip}，登录时间: ${at}）。\n是否将该设备下线并继续登录本机？`,
      '检测到重复登录',
      { confirmButtonText: '下线旧设备并登录', cancelButtonText: '取消', type: 'warning' }
    )
    return await request({
      url: '/api/auth/login',
      method: 'post',
      data: payloadBuilder(true)
    })
  }
}

const openResetDialog = () => {
  showResetDialog.value = true
}

// 发送短信验证码
const sendSmsCode = async () => {
  if (!smsFormRef.value) return
  
  await smsFormRef.value.validateField('phone', async (valid) => {
    if (valid) {
      sendingSms.value = true
      try {
        const response = await request({
          url: '/api/auth/send-sms-code',
          method: 'post',
          data: { phone: smsForm.phone }
        })
        
        if (response.success) {
          ElMessage.success('验证码已发送')
          // 开始倒计时
          startCountdown(smsCountdown)
          
          // 开发环境显示验证码
          if (response.code) {
            ElMessage.info(`验证码：${response.code}（仅开发环境显示）`)
          }
        } else {
          ElMessage.error(response.message || '发送验证码失败')
        }
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '发送验证码失败')
      } finally {
        sendingSms.value = false
      }
    }
  })
}

// 密码登录
const handlePasswordLogin = async () => {
  if (!passwordFormRef.value) return
  
  await passwordFormRef.value.validate(async (valid) => {
    if (valid) {
      loading.value = true
      try {
        const response = await withSessionControlLogin((forceLogoutExisting) => ({
          login_type: 'password',
          username: passwordForm.username,
          password: passwordForm.password,
          force_logout_existing: forceLogoutExisting,
          device_type: inferDeviceType(),
          device_name: getDeviceName(),
          device_fingerprint: getDeviceFingerprint()
        }))
        
        if (response.success) {
          ElMessage.success('登录成功')
          try {
            if (rememberUsername.value) {
              localStorage.setItem(REMEMBER_USERNAME_KEY, '1')
              localStorage.setItem(REMEMBERED_USERNAME_KEY, passwordForm.username || '')
            } else {
              localStorage.removeItem(REMEMBER_USERNAME_KEY)
              localStorage.removeItem(REMEMBERED_USERNAME_KEY)
            }
          } catch (_) {
            /* ignore */
          }

          // 仅本机系统密码库；失败/拒绝不阻断登录
          await tryStorePasswordInSystemVault(passwordForm.username, passwordForm.password)
          
          userStore.setToken(response.token)
          userStore.setUser(response.user)
          localStorage.setItem('app_build_id', APP_BUILD_ID)
          markUserActive()
          
          // 设置axios默认授权头
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.token}`
          // 以服务端当前用户为准，避免同浏览器上一账号权限/菜单残留
          await userStore.syncUserFromServer({ force: true })
          
          if (response.must_change_password || response.user?.must_change_password) {
            ElMessage.warning('请先修改初始密码')
            router.push('/change-password')
            return
          }
          navigatePostLogin(resolvePostLoginPath(route.query.redirect, response.user))
        } else {
          ElMessage.error(response.message || '登录失败')
        }
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '登录失败，请检查网络连接')
      } finally {
        loading.value = false
      }
    }
  })
}

// 短信登录
const handleSmsLogin = async () => {
  if (!smsFormRef.value) return
  
  await smsFormRef.value.validate(async (valid) => {
    if (valid) {
      loading.value = true
      try {
        const response = await withSessionControlLogin((forceLogoutExisting) => ({
          login_type: 'sms',
          phone: smsForm.phone,
          sms_code: smsForm.sms_code,
          force_logout_existing: forceLogoutExisting,
          device_type: inferDeviceType(),
          device_name: getDeviceName(),
          device_fingerprint: getDeviceFingerprint()
        }))
        
        if (response.success) {
          ElMessage.success('登录成功')
          
          userStore.setToken(response.token)
          userStore.setUser(response.user)
          localStorage.setItem('app_build_id', APP_BUILD_ID)
          markUserActive()
          
          // 设置axios默认授权头
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.token}`
          await userStore.syncUserFromServer({ force: true })
          
          if (response.must_change_password || response.user?.must_change_password) {
            ElMessage.warning('请先修改初始密码')
            router.push('/change-password')
            return
          }
          navigatePostLogin(resolvePostLoginPath(route.query.redirect, response.user))
        } else {
          ElMessage.error(response.message || '登录失败')
        }
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '登录失败，请检查网络连接')
      } finally {
        loading.value = false
      }
    }
  })
}

// 发送重置密码验证码
const sendResetSmsCode = async () => {
  if (!resetFormRef.value) return
  await resetFormRef.value.validateField('phone', async (valid) => {
    if (valid) {
      sendingResetSms.value = true
      try {
        const response = await request({
          url: '/api/auth/send-sms-code',
          method: 'post',
          data: { phone: resetForm.phone, purpose: 'reset_password' }
        })
        if (response.success) {
          ElMessage.success('验证码已发送')
          startCountdown(resetSmsCountdown)
          if (response.code) {
            ElMessage.info(`验证码：${response.code}（仅开发环境显示）`)
          }
        } else {
          ElMessage.error(response.message || '发送验证码失败')
        }
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '发送验证码失败')
      } finally {
        sendingResetSms.value = false
      }
    }
  })
}

const handleResetPasswordBySms = async () => {
  if (!resetFormRef.value) return
  await resetFormRef.value.validate(async (valid) => {
    if (!valid) return
    resettingPassword.value = true
    try {
      const response = await request({
        url: '/api/auth/reset-password-by-sms',
        method: 'post',
        data: {
          phone: resetForm.phone,
          sms_code: resetForm.sms_code,
          new_password: resetForm.new_password
        }
      })
      if (response.success) {
        ElMessage.success('密码重置成功，请使用新密码登录')
        showResetDialog.value = false
      } else {
        ElMessage.error(response.message || '重置失败')
      }
    } catch (error) {
      ElMessage.error(error.response?.data?.message || '重置失败')
    } finally {
      resettingPassword.value = false
    }
  })
}

onMounted(async () => {
  nextTick(() => resetBodyElLoadingArtifacts())
  try {
    const remember = localStorage.getItem(REMEMBER_USERNAME_KEY) === '1'
    const savedUsername = localStorage.getItem(REMEMBERED_USERNAME_KEY) || ''
    rememberUsername.value = remember
    if (remember && savedUsername) {
      passwordForm.username = savedUsername
    }
  } catch (_) {
    /* ignore */
  }
  // 系统密码库自动填充（不写网页本地）；用户可取消
  try {
    const vault = await tryLoadPasswordFromSystemVault()
    if (vault) {
      passwordForm.username = vault.username
      passwordForm.password = vault.password
      rememberUsername.value = true
    }
  } catch (_) {
    /* ignore */
  }
  /** 已登录时点浏览器返回仍会回到登录页：直接送回移动工作台（与 navigatePostLogin 的 replace 配合） */
  try {
    const tok = localStorage.getItem('token')
    if (tok && isLikelyMobileBrowser() && route.path === '/login') {
      userStore.initFromStorage()
      await userStore.syncUserFromServer({ force: false })
      const u = userStore.user
      if (!u || u.must_change_password) return
      const dest = resolvePostLoginPath(route.query.redirect, u)
      if (
        typeof dest === 'string' &&
        dest.startsWith('/m') &&
        canAccessMobilePath(dest, u) &&
        dest !== '/login'
      ) {
        await router.replace(dest)
      }
    }
  } catch (_) {
    /* ignore */
  }
})
</script>

<style scoped>
.login-container {
  height: 100%;
  min-height: 100%;
  min-height: 100dvh;
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-bottom: calc(56px + env(safe-area-inset-bottom, 0));
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-sizing: border-box;
}

.login-box {
  width: 420px;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.login-title {
  text-align: center;
  margin-bottom: 30px;
  color: #333;
  font-size: 24px;
  font-weight: 600;
}

.login-native-form {
  margin: 0;
  padding: 0;
}

.login-secure-hint {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: #909399;
}

.login-tabs {
  margin-top: 10px;
}

.sms-code-input {
  display: flex;
  gap: 10px;
}

.sms-code-input :deep(.el-input) {
  flex: 1;
}

.sms-button {
  white-space: nowrap;
}

.login-help-row {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.help-text {
  color: #909399;
  font-size: 12px;
}

</style>
